import useSWRImmutable from "swr/immutable";

import { defaultFetcher } from "../utils";
import type { HTTPClientV3 } from "../HTTPClientV3";
import { useApiClientContext } from "../../app/ApiClientCtx";
import { serializers, JSONAPIDocument } from "../Serializer";
import { ServerError } from "../ServerError";
import type { Template } from "./template";
import { buildUrlWithQueryParams } from "../QueryParams";
import type { BaseLanguage } from "../../helpers/i18n";

export const REVIEWS_ALLOWED_FEATURE_KEY = "reviews";

export enum RedemptionFlow {
  Email = "email",
  Classic = "classic",
}

interface File {
  legend: string;
  url: string;
  filename: string;
}

export interface EmbedUrl {
  id?: string;
  url: string;
}

export const PHONE_VALIDATION_RULE_MAPPING = {
  numbers_only: /^\d+$/,
  numbers_letters_spaces: /^[a-zA-Z0-9\s]+$/,
  numbers_letters_spaces_dash: /^[a-zA-Z0-9\s+().\-#/,]*$/,
};

// TODO: rename all this file to Marketplace
export interface Configuration {
  id: string;
  limited: boolean;
  isOpenMarketplace: boolean;
  forceAccounts: boolean;
  redirectUrl: string;
  redemptionFlow: RedemptionFlow;
  publishedTemplate: Template;
  subdomain: string;
  customDomain: string;
  website: string;
  signUpLogoUrl: string;
  homePageLogoUrl: string;
  name: string;
  description: string;
  embedUrls: EmbedUrl[];
  staticPhrases: BaseLanguage;
  tagline: string;
  ssoSetting: {
    forceJwtAuth: boolean;
    signInRedirectUrl: string;
    signUpRedirectUrl: string;
    googleClientId: string;
    microsoftClientId: string;
    useDefaultMicrosoftClient: boolean;
    useDefaultGoogleClient: boolean;
  };
  brandingSetting: {
    id: string;
    primaryColor: string;
    secondaryColor: string;
    bodyFontFamily: string;
    headerFontFamily: string;
    poweredByBuiltfirstLabel: boolean;
  };
  communityToggleSetting: {
    redemptionGating: boolean;
    multipleRedemptionAllowed: boolean;
    unsafeInlineStyleAllowed: boolean;
    unsafeEvalAllowed: boolean;
    seoDisableIndexing: boolean;
    contentSecurityPolicyEnabled: boolean;
    contentSecurityPolicy: {
      frameSrc: string[];
      styleSrc: string[];
      imgSrc: string[];
      fontSrc: string[];
      mediaSrc: string[];
      frameAncestors: string[];
      connectSrc: string[];
      formAction: string[];
    };
    phoneValidationRule: keyof typeof PHONE_VALIDATION_RULE_MAPPING;
    phoneValidationMinLength: number;
    phoneValidationMaxLength: number;
  };
  managerOrganization: {
    name: string;
    logoUrl: string;
    symbolLogoUrl: string;
  };
  mediaFiles: File[];
  fonts: File[];
  allowedFeatures: ("reviews" | "quotes")[];
}

export const communitiesFallbackKey = "/communities";

export async function pingServer() {
  return fetch(
    `${process.env.NEXT_PUBLIC_API_URL_V3}${communitiesFallbackKey.replace(
      "/",
      "",
    )}`,
  );
}

export function getConfiguration(APIV3: HTTPClientV3) {
  const url = buildUrlWithQueryParams(communitiesFallbackKey, {
    include:
      "manager_organization,media_files,fonts,embed_urls,branding_setting,community_toggle_setting",
    "fields[community]": [
      "is_open_marketplace",
      "limited",
      "force_accounts",
      "redirect_url",
      "redemption_flow",
      "community_toggle_setting",
      "published_template",
      "subdomain",
      "custom_domain",
      "website",
      "sign_up_logo_url",
      "home_page_logo_url",
      "name",
      "description",
      "tagline",
      "media_files",
      "fonts",
      "embed_urls",
      "allowed_features",
      "sso_setting",
      "branding_setting",
      "manager_organization",
      "static_phrases",
    ].join(),
    "fields[sso_setting]": [
      "force_jwt_auth",
      "sign_in_redirect_url",
      "sign_up_redirect_url",
      "google_client_id",
      "microsoft_client_id",
      "use_default_microsoft_client",
      "use_default_google_client",
    ].join(),
    "fields[branding_setting]": [
      "primary_color",
      "secondary_color",
      "body_font_family",
      "header_font_family",
      "powered_by_builtfirst_label",
    ].join(),
    "fields[manager_organization]": [
      "name",
      "logo_url",
      "symbol_logo_url",
    ].join(),
    "fields[community_toggle_setting]": [
      "redemption_gating",
      "multiple_redemption_allowed",
      "unsafe_inline_style_allowed",
      "unsafe_eval_allowed",
      "seo_disable_indexing",
      "content_security_policy",
      "content_security_policy_enabled",
      "phone_validation_rule",
      "phone_validation_min_length",
      "phone_validation_max_length",
    ].join(),
  });
  const request = () => APIV3.get<JSONAPIDocument<Configuration>>(url);

  return {
    json: () => defaultFetcher(request, serializers.community),
    fallbackKey: communitiesFallbackKey,
  };
}

export function useConfiguration() {
  const { APIV3 } = useApiClientContext();
  const { fallbackKey, json } = getConfiguration(APIV3);
  const { data, error, isValidating, mutate } = useSWRImmutable(
    fallbackKey,
    json,
    {
      revalidateOnMount: false,
    },
  );

  if (!(error instanceof ServerError)) {
    return {
      loadingConfiguration: isValidating,
      setConfiguration: mutate,
      configuration: data?.data,
    };
  }
  return {
    loadingConfiguration: isValidating,
    setConfiguration: mutate,
    error,
  };
}
